import React, { useMemo } from "react";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import "./styles.scss";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import {
  KC_Sizes,
  KC_Text_Sizes,
  KC_Weights
} from "../../../../designsystem/ds_commons";
import KC_Heading4 from "../../../../designsystem/typography/KC-Heading/KC_Heading4";
import KC_Colors from "../../../../designsystem/KC_Colors";
import KC_Text from "../../../../designsystem/typography/KC_Text/KC_Text";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import TSUtils from "../../../../utils/helpers/TSUtils";
import SubscriptionPlanDTO, {
  SubscriptionPlanType
} from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import { SaasCommitment } from "../../../../model/SaasTypes";
import KC_UISmall from "../../../../designsystem/typography/KC_UITextSmall/KC_UISmall";
import { LANGUAGE, tt } from "../../../../utils/i18n/i18n";
import { ComputePricesResponse } from "../../../../components/CodePromo/api";
import GridItem from "./components/GridItem";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";

interface Props {
  plan: Partial<SubscriptionPlanDTO>;
  duration: SaasCommitment;
  startTs: number;
  renewalTs: number;
  mustPayVAT: boolean;
  computePricesResponse: ComputePricesResponse;
  isFirstMonthFree?: boolean;
  firstPaymentDate?: Date;
  isFreeLicense?: boolean;
}

export default function OrderSummary(props: Props) {
  const { accountToDisplay } = useAccountToDisplaySelector();

  const commitmentType = useMemo(() => {
    switch (props.duration) {
      case "yearly":
        return tt("order_summary_annual");
      case "monthly":
        return tt("order_summary_monthly");
      default:
        return "";
    }
  }, [props.duration]);

  const preTaxAmountWording = useMemo(() => {
    switch (props.duration) {
      case "yearly":
        return tt("order_summary_monthly_payment_pre_tax");
      case "monthly":
        return tt("order_summary_total_pre_tax");
      default:
        return "";
    }
  }, [props.duration]);

  const postTaxAmountWording = useMemo(() => {
    switch (props.duration) {
      case "yearly":
        return tt("order_summary_monthly_payment_post_tax");
      case "monthly":
        return tt("order_summary_total_post_tax");
      default:
        return "";
    }
  }, [props.duration]);

  const featuresList = useMemo(() => {
    if (props.plan?.type !== SubscriptionPlanType.CUSTOM) {
      return "";
    }

    const res: string[] = [];

    /**
     * Premium => different wording
     */
    if (props.plan.saasFeatures?.ip) {
      res.push(tt("order_summary_pack_premium"));
    }

    props.plan.includedOptions?.forEach((opt) => {
      if (opt.isDefault) {
        return;
      }
      res.push(opt.nameL?.[LANGUAGE] || opt.name);
    });

    /**
     * Premium => different wording
     */
    if (props.plan.saasFeatures?.ip) {
      return res.join(" + ");
    }

    if (props.plan.saasFeatures?.t) {
      res.push(tt("order_summary_custom_logo_and_colors"));
    }

    if (props.plan.saasFeatures?.s) {
      res.push(tt("order_summary_themed_skins"));
    }

    if (props.plan.saasFeatures?.cp) {
      res.push(tt("order_summary_custom_communication_materials"));
    }

    if (props.plan.saasFeatures?.gc) {
      res.push(tt("order_summary_games_of_chance"));
    }

    if (props.plan.saasFeatures?.pg) {
      res.push(tt("order_summary_performance_games"));
    }

    if (props.plan.saasFeatures?.ml) {
      res.push(tt("order_summary_monthly_jackpots"));
    }

    if (props.plan.saasFeatures?.ma) {
      res.push(tt("order_summary_advanced_email_sequences"));
    }

    if (props.plan.saasFeatures?.ac) {
      res.push(tt("order_summary_all_actions"));
    } else {
      if (props.plan.saasFeatures?.ctas) {
        const keys = Object.keys(props.plan.saasFeatures.ctas);
        res.push(tt("order_summary_calls_to_action", { count: keys.length }));
      }
    }

    return res.join(", ");
  }, [props.plan]);

  const isUpsell = useMemo(() => {
    return !!props.computePricesResponse?.currentSub;
  }, [props.computePricesResponse]);

  if (!accountToDisplay) {
    return null;
  }

  return (
    <div
      id="order-summary"
      className="p-4 checkout-info-form h-100 d-flex flex-column st-gap"
    >
      <div>
        <KC_Heading3 text={tt("order_summary_order")} />
        <KC_Separator size={KC_Sizes.sm} />
      </div>

      <div>
        <div className="d-flex flex-column gap-sm">
          <div className="w-100 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column">
              {props.plan?.type === SubscriptionPlanType.CUSTOM && (
                <KC_Text
                  size={KC_Text_Sizes.lg}
                  weight={KC_Weights.bold}
                  text={tt("order_summary_pack_enterprise")}
                />
              )}
              {props.plan?.type !== SubscriptionPlanType.CUSTOM && (
                <KC_Text
                  size={KC_Text_Sizes.lg}
                  weight={KC_Weights.reg}
                  text={tt("pack_label")}
                />
              )}
              {!!featuresList && (
                <KC_UISmall
                  weight={KC_Weights.reg}
                  text={featuresList}
                  className="mb-2"
                />
              )}
            </div>
            <div className="flex-grow-1">&nbsp;</div>
            <div className="">
              {!!props.computePricesResponse?.currentSub && (
                <>
                  {tt("order_summary_from")}
                  <KC_UIText
                    weight={KC_Weights.bold}
                    text={
                      props.plan?.type === SubscriptionPlanType.CUSTOM
                        ? tt("custom")
                        : props.computePricesResponse?.currentSub
                    }
                  />
                  {tt("order_summary_to")}
                </>
              )}
              <KC_UIText
                weight={KC_Weights.bold}
                text={
                  props.plan?.type === SubscriptionPlanType.CUSTOM
                    ? ""
                    : props.computePricesResponse?.nextSub
                }
              />
            </div>
          </div>
          {!props.isFreeLicense && (
            <div className="w-100 d-flex justify-content-between">
              <KC_Text
                size={KC_Text_Sizes.md}
                weight={KC_Weights.reg}
                text={tt("commitment_label")}
              />
              <KC_UIText weight={KC_Weights.bold} text={commitmentType} />
            </div>
          )}
          {props.isFirstMonthFree && (
            <div className="w-100 d-flex justify-content-between">
              <KC_Text
                size={KC_Text_Sizes.md}
                weight={KC_Weights.reg}
                text={tt("order_summary_free_month")}
                className="text-red"
              />
              <KC_UIText
                weight={KC_Weights.bold}
                text={TSUtils.timestampToDateStringAbbreviated(props.startTs)}
                className="text-red"
              />
            </div>
          )}
          <div className="w-100 d-flex justify-content-between">
            <KC_Text
              size={KC_Text_Sizes.md}
              weight={KC_Weights.reg}
              text={tt("subscription_start_label")}
            />

            <KC_UIText
              weight={KC_Weights.bold}
              text={TSUtils.timestampToDateStringAbbreviated(
                props.isFirstMonthFree && props.firstPaymentDate
                  ? Math.floor(props.firstPaymentDate.getTime() / 1000)
                  : props.startTs
              )}
            />
          </div>
          {!props.isFreeLicense && (
            <div className="w-100 d-flex justify-content-between">
              <KC_Text
                size={KC_Text_Sizes.md}
                weight={KC_Weights.reg}
                text={tt("commitment_end_label")}
              />
              <KC_UIText
                weight={KC_Weights.bold}
                text={TSUtils.timestampToDateStringAbbreviated(props.renewalTs)}
              />
            </div>
          )}
        </div>
        <KC_Separator
          size={KC_Sizes.sm}
          hasLine={true}
          color={KC_Colors.Black}
        />
        <KC_Separator size={KC_Sizes.sm} />
      </div>

      {!!props.computePricesResponse.discountPcBase100 && (
        <>
          <div className="w-100 d-flex justify-content-between">
            <div className="d-flex">
              <KC_Heading4 text={tt("public_price_ht")} />
            </div>
            <KC_UIText
              weight={KC_Weights.bold}
              text={TSUtils.formatAmountWithLocalizedFormatter(
                props.computePricesResponse.nextSubPriceBeforeDiscount,
                accountToDisplay.curSubUnits,
                accountToDisplay.currency
              )}
            />
          </div>
          <div className="w-100 d-flex justify-content-between">
            <div className="d-flex">
              <KC_Heading4
                text={tt("order_summary_discount", {
                  percent: props.computePricesResponse.discountPcBase100
                })}
                className="text-red"
              />
            </div>
            <KC_UIText
              id="discount-amount"
              weight={KC_Weights.bold}
              className="text-red"
              text={`- ${TSUtils.formatAmountWithLocalizedFormatter(
                props.computePricesResponse.nextSubPriceBeforeDiscount -
                  props.computePricesResponse.nextSubPriceExclVAT,
                accountToDisplay.curSubUnits,
                accountToDisplay.currency
              )}`}
            />
          </div>
          <KC_Separator
            size={KC_Sizes.sm}
            hasLine={true}
            color={KC_Colors.Black}
          />
          <KC_Separator size={KC_Sizes.sm} />
        </>
      )}

      <GridItem
        pattern="40% 60%"
        left={<KC_Heading4 text={preTaxAmountWording} />}
        right={
          <div className="text-right">
            {isUpsell && tt("order_summary_this_month")}
            <KC_UIText
              weight={KC_Weights.bold}
              text={TSUtils.formatAmountWithLocalizedFormatter(
                props.computePricesResponse?.upsellExclVAT,
                accountToDisplay.curSubUnits,
                accountToDisplay.currency
              )}
            />
            {isUpsell && (
              <>
                <br />
                {tt("order_summary_starting_next_month")}
                <KC_UIText
                  weight={KC_Weights.bold}
                  text={TSUtils.formatAmountWithLocalizedFormatter(
                    props.computePricesResponse?.nextSubPriceExclVAT,
                    accountToDisplay.curSubUnits,
                    accountToDisplay.currency
                  )}
                />
              </>
            )}
          </div>
        }
      />

      <GridItem
        pattern="40% 60%"
        left={
          <KC_Heading4
            text={tt("order_summary_vat", {
              percent: props.mustPayVAT ? "20%" : "0%"
            })}
          />
        }
        right={
          <div className="text-right">
            {isUpsell && tt("order_summary_this_month")}
            <KC_UIText
              id="tva-amount"
              weight={KC_Weights.bold}
              text={TSUtils.formatAmountWithLocalizedFormatter(
                props.computePricesResponse.upsellVAT,
                accountToDisplay.curSubUnits,
                accountToDisplay.currency
              )}
            />{" "}
            {isUpsell && (
              <>
                <br />
                {tt("order_summary_starting_next_month")}
                <KC_UIText
                  id="tva-amount-2"
                  weight={KC_Weights.bold}
                  text={TSUtils.formatAmountWithLocalizedFormatter(
                    props.computePricesResponse.nextSubPriceVAT,
                    accountToDisplay.curSubUnits,
                    accountToDisplay.currency
                  )}
                />
              </>
            )}
          </div>
        }
      />

      <KC_Separator size={KC_Sizes.sm} hasLine={true} color={KC_Colors.Black} />

      <GridItem
        pattern="40% 60%"
        left={<KC_Heading3 text={postTaxAmountWording} />}
        right={
          <div className="text-right">
            {isUpsell && tt("order_summary_this_month")}
            <KC_UIText
              weight={KC_Weights.bold}
              text={TSUtils.formatAmountWithLocalizedFormatter(
                props.computePricesResponse?.upsellInclVAT,
                accountToDisplay.curSubUnits,
                accountToDisplay.currency
              )}
            />
            {isUpsell && (
              <>
                <br />
                {tt("order_summary_starting_next_month")}
                <KC_UIText
                  weight={KC_Weights.bold}
                  text={TSUtils.formatAmountWithLocalizedFormatter(
                    props.computePricesResponse?.nextSubPriceInclVAT,
                    accountToDisplay.curSubUnits,
                    accountToDisplay.currency
                  )}
                />
              </>
            )}
          </div>
        }
      />

      {props.isFirstMonthFree && props.firstPaymentDate && (
        <div className="w-100 d-flex justify-content-start">
          <KC_Heading4 className="text-red" text={tt("payable_from_date")} />
        </div>
      )}
    </div>
  );
}
